import { StatusCodes } from 'http-status-codes';

import ApiService from 'services/ApiService/ApiService';
import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import { translate } from 'services/i18nService';
import MessagingService from 'services/MessagingService/MessagingService';
import {
  CREATE_GALLERY_ERROR,
  CREATE_GALLERY_RESPONSE,
  CREATE_PHOTO_ERROR,
  CREATE_PHOTO_RESPONSE,
  GET_GALLERIES,
  GET_GALLERIES_DELETE_PHOTO_STATE,
  GET_GALLERIES_ERROR,
  GET_GALLERIES_RESPONSE,
  GET_PHOTOS,
} from 'services/MessagingService/EventNames';
import ModalService from 'services/ModalService';
import NotificationService from 'services/NotificationService';
import APIStates from 'enum/APIStates';
import parseCatch from 'utils/parseCatch';


const BASE_URL = ConfigurationService.get(ConfigurationKeys.BASE_URL);

const GALLERIES_URL = `${BASE_URL}/rest/v2.0/users/{username}/galleries`;
const GALLERIES_UPDATE_URL = `${BASE_URL}/rest/v2.0/users/{username}/galleries/{galleryId}`;
const IMAGE_DELETE_URL = `${BASE_URL}/rest/v2.0/users/{username}/galleries/{galleryId}/images/{imageId}`;
const PHOTOS_URL = `${BASE_URL}/rest/v2.0/users/{username}/galleries/{galleryId}/images`;

/**
 * @class handles business logic for galleries and photos and expose methods
 */
class GalleryService {
  constructor(messagingService) {
    this.messagingService = messagingService();
  }

  /**
   * Method for creating a new gallery
   * @param {string} username
   * @param {string} galleryName
   * @param {string} galleryType
   * @emits CREATE_GALLERY_RESPONSE when API call is successful
   * @emits CREATE_GALLERY_ERROR when API call fails
   */
  createGallery = (username, galleryName, galleryType) => {
    const URL = ApiService.substitute(GALLERIES_URL, { username: encodeURIComponent(username) });

    ApiService.post(URL, { data: { name: galleryName, galleryType } })
      .then((response) => {
        if (response.status === StatusCodes.OK) {
          this.messagingService.emit(CREATE_GALLERY_RESPONSE, response.data);
        } else {
          this.messagingService.emit(CREATE_GALLERY_ERROR, response);
        }
      })
      .catch((error) => {
        this.messagingService.emit(CREATE_GALLERY_ERROR, error);
        parseCatch(error);
      });
  };

  /**
   * Method for getting all available galleries for given user
   * @param {string} username
   * @emits GET_GALLERIES_RESPONSE when API call is successful
   * @emits GET_GALLERIES_ERROR when API call fails
   */
  getGalleries = (username) => {
    const URL = ApiService.substitute(GALLERIES_URL, { username: encodeURIComponent(username) });

    ApiService.get(URL)
      .then((response) => {
        if (response.status === StatusCodes.OK) {
          this.messagingService.emit(GET_GALLERIES_RESPONSE, response.data);
        } else {
          this.messagingService.emit(GET_GALLERIES_ERROR, response.data);
        }
      })
      .catch((error) => {
        this.messagingService.emit(GET_GALLERIES_ERROR, error);
        parseCatch(error);
      });
  };

  /**
   *
   * @param {string} username current username
   * @param {number} galleryId id of the gallery that you want to update
   * @param {object} data object that with properties for updating
   * @emits GET_GALLERIES get updated galleries after updating
   * @emits GET_PHOTOS get updated galleries after updating
   */
  updateGallery = (username, galleryId, data) => {
    const URL = ApiService.substitute(GALLERIES_UPDATE_URL,
      { username: encodeURIComponent(username), galleryId: encodeURIComponent(galleryId) });

    ApiService.put(URL, { data })
      .then(() => {
        NotificationService.create({
          type: NotificationService.type.TOAST,
          subtype: NotificationService.subtype.SUCCESS,
          text: translate('account.changesSaved'),
        });
        this.messagingService.emit(GET_PHOTOS, { username, galleryId });
        this.messagingService.emit(GET_GALLERIES, username);
        this.messagingService.emit(GET_GALLERIES_DELETE_PHOTO_STATE, APIStates.LOADED);
      })
      .catch((error) => parseCatch(error));
  };

  /**
   *
   * @param {string} username current username
   * @param {number} galleryId id of the gallery that you want to delete gallery
   * @emits GET_GALLERIES_DELETE_PHOTO_STATE get status of deleting galleries endpoint
   * @emits GET_GALLERIES get updated galleries after deleting
   */
  deleteGallery = (username, galleryId) => {
    const URL = ApiService.substitute(GALLERIES_UPDATE_URL,
      { username: encodeURIComponent(username), galleryId: encodeURIComponent(galleryId) });
    this.messagingService.emit(GET_GALLERIES_DELETE_PHOTO_STATE, APIStates.PENDING);

    ApiService.delete(URL)
      .then(() => {
        NotificationService.create({
          type: NotificationService.type.TOAST,
          subtype: NotificationService.subtype.SUCCESS,
          text: translate('account.changesSaved'),
        });
        this.messagingService.emit(GET_GALLERIES, username);
        this.messagingService.emit(GET_GALLERIES_DELETE_PHOTO_STATE, APIStates.LOADED);
        ModalService.hideModal();
      })
      .catch(() => {
        this.messagingService.emit(GET_GALLERIES_DELETE_PHOTO_STATE, APIStates.ERROR);
        NotificationService.create({
          type: NotificationService.type.TOAST,
          subtype: NotificationService.subtype.ERROR,
          text: translate('v2profile.notification.error.default'),
        });
      });
  };

  /**
   *
   * @param {string} username current username
   * @param {string} galleryId id of the gallery that you want to add image
   * @param {file} image upload file object
   * @param {function} cb send image id and progres (percent) when image is uploading
   * @emits CREATE_PHOTO_RESPONSE when API call is successful
   * @emits CREATE_PHOTO_ERROR when API call fails
   */
  createPhoto = (username, galleryId, image, cb) => {
    const URL = ApiService.substitute(PHOTOS_URL, {
      username: encodeURIComponent(username),
      galleryId: encodeURIComponent(galleryId),
    });

    const formData = new FormData();
    formData.append('image', image);
    const config = {
      headers: { 'content-type': 'multipart/form-data' },
      withCredentials: true,
      data: formData,
      onUploadProgress(progressEvent) {
        let totalLength = undefined;
        if (progressEvent.lengthComputable) {
          totalLength = progressEvent.total;
        } else {
          totalLength = progressEvent.target.getResponseHeader('content-length') ||
            progressEvent.target.getResponseHeader('x-decompressed-content-length');
        }

        let progress = 0;
        if (totalLength !== null) {
          progress = Math.round((progressEvent.loaded * 100) / totalLength);
        }
        cb(image.id, progress);
      },
    };

    ApiService.post(URL, config)
      .then((response) => {
        if (response.status === StatusCodes.OK) {
          this.messagingService.emit(CREATE_PHOTO_RESPONSE, response);
        } else {
          this.messagingService.emit(CREATE_PHOTO_ERROR, response);
        }
      })
      .catch((error) => {
        this.messagingService.emit(CREATE_PHOTO_ERROR, error);
        parseCatch(error);
      });
  };

  /**
   * @todo Write the documentation.
   * @todo Implement this function.
   */
  getPhotos = () => undefined;

  /**
   *
   * @param {string} username current username
   * @param {number} galleryId id of the gallery
   * @param {number} imageId id of the image you want to delete
   * @param {number} nextImageId id of the image you want to set as main image
   * @param {string} galleryName gallery name for updating the gallery after deleting photo
   * @param {string} galleryType gallery type for updating the gallery after deleting photo
   * @emits GET_GALLERIES_DELETE_PHOTO_STATE get status of deleting photos endpoint
   * @emits GET_PHOTOS get updated photos after deleting
   * @emits GET_PHOTOS get updated galleries after deleting
   */
  deletePhoto = (username, galleryId, imageId, nextImageId, galleryName, galleryType) => {
    const URL = ApiService.substitute(IMAGE_DELETE_URL, {
      username: encodeURIComponent(username),
      galleryId: encodeURIComponent(galleryId),
      imageId: encodeURIComponent(imageId),
    });
    this.messagingService.emit(GET_GALLERIES_DELETE_PHOTO_STATE, APIStates.PENDING);
    ApiService.delete(URL)
      .then(() => {
        if (nextImageId) {
          const data = {
            galleryName,
            galleryType,
            mainImageId: nextImageId,
          };
          this.updateGallery(username, galleryId, data);
        } else {
          NotificationService.create({
            type: NotificationService.type.TOAST,
            subtype: NotificationService.subtype.SUCCESS,
            text: translate('account.changesSaved'),
          });
          this.messagingService.emit(GET_PHOTOS, { username, galleryId });
          this.messagingService.emit(GET_GALLERIES, username);
          this.messagingService.emit(GET_GALLERIES_DELETE_PHOTO_STATE, APIStates.LOADED);
        }
        ModalService.hideModal();
      })
      .catch(() => {
        this.messagingService.emit(GET_GALLERIES_DELETE_PHOTO_STATE, APIStates.ERROR);
        NotificationService.create({
          type: NotificationService.type.TOAST,
          subtype: NotificationService.subtype.ERROR,
          text: translate('v2profile.notification.error.default'),
        });
      });
  };
}

export default new GalleryService(MessagingService);
